import React from "react";
import * as Yup from "yup";
import { INewTradedAccountForm } from "./components/TradedAccounts";
import { fundedTraderActions } from "../../reducers/fundedTrader";
import { APIFunds } from "../../api";
import { EPProcessingCompletedStatuses, EProcessingStatuses } from "../../constants";
import { ITradedAccount } from "../../api/funds/index.types";
import { ISelectOption } from "../../components/ui/FormSelect";
export const ACCOUNTS_SCHEMA = Yup.object().shape({
  e2t_account_id: Yup.string().required("Account ID is required").min(3),
  prop_firm: Yup.string().required("Prop firm is required"),
  account_type: Yup.string().required("Account type is required"),
  processing_status: Yup.string().required("Processing status is required"),
  invoice_status: Yup.string().required("Invoice status is required"),
  account_drawdown: Yup.string().required("Drawdown is required"),
  evaluation_completed_history_id: Yup.number()
    .min(1, "E2T account name is required")
    .required("E2T account name is required"),
  evaluation_type: Yup.string().required("Evaluation type is required"),
});

export const getAccountsInitialValues = (customerId: number): INewTradedAccountForm => ({
  e2t_account_id: "",
  prop_firm: "",
  account_type: "",
  processing_status: "",
  funded_traders_id: customerId,
  original_account: "",
  invoice_status: "",
  evaluation_completed_history_id: 0,
  initial_tcp_account_id: "",
  evaluation_type: "",
});

export const fetchTradedAccounts = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: React.Dispatch<any>,
  customerId: number,
  query?: string
): Promise<void> => {
  try {
    dispatch(fundedTraderActions.accountsFetchRequest());
    const query = new URLSearchParams();
    query.append("funded_traders_id", customerId.toString());
    const response = await APIFunds.getTradedAccountsRequest(customerId, query.toString());
    dispatch(fundedTraderActions.accountsFetchSuccess(response.data.traded_accounts));
  } catch (error) {
    console.error("Error getting traded accounts", error);
  } finally {
    dispatch(fundedTraderActions.accountsFetchFailed());
  }
};

type ICheckIsCompletedAccount = (
  processing_status: EProcessingStatuses,
  original_account?: string | null,
  account?: ITradedAccount,
  tradedAccounts?: ITradedAccount[]
) => boolean;
export const checkIsCompletedAccount: ICheckIsCompletedAccount = (
  processing_status,
  original_account,
  account,
  tradedAccounts
): boolean => {
  if (original_account?.includes("TCP") && account?.id && tradedAccounts?.length) {
    const subAccounts: ITradedAccount[] = [];

    if (account?.initial_tcp_account_id) {
      const accounts = tradedAccounts.filter(
        (ta) =>
          ta.initial_tcp_account_id === account?.initial_tcp_account_id ||
          ta.id === account?.initial_tcp_account_id
      );
      subAccounts.push(...accounts);
    } else {
      const accounts = tradedAccounts.filter((ta) => ta.initial_tcp_account_id === account.id);
      subAccounts.push(account, ...accounts);
    }

    const allExceptLatest = subAccounts.slice(0, -1);
    const latestAccount = subAccounts.at(-1);

    return (
      latestAccount?.processing_status === EProcessingStatuses.failed &&
      allExceptLatest.every(({ processing_status }) =>
        [EProcessingStatuses.completed, EProcessingStatuses.failed].includes(
          processing_status as EProcessingStatuses
        )
      )
    );
  }
  if (original_account?.includes("GM") && processing_status === EProcessingStatuses.completed) {
    return true;
  }
  return EPProcessingCompletedStatuses.includes(processing_status);
};

export const getTradedAccountsOptions = (tradedAccountsData: ITradedAccount[]): ISelectOption[] => {
  const options = [
    {
      value: "",
      label: "No",
      id: -1,
    },
  ];
  const tradedAccounts = tradedAccountsData
    .filter(
      (ta) =>
        !checkIsCompletedAccount(ta.processing_status, ta.original_account, ta, tradedAccountsData)
    )
    .filter(
      ({ original_account, processing_status, initial_tcp_account_id }) =>
        original_account?.includes("TCP") &&
        processing_status !== EProcessingStatuses.failed &&
        !initial_tcp_account_id
    )
    .map((account) => ({
      value: account.id,
      label: account.e2t_account_id,
      id: account.id,
    }));

  return [...options, ...tradedAccounts];
};

export const MAXIMUM_FUNDED_ACCOUNTS_COUNT = 3;
